// API Security — Data Loss Prevention (Phase 4) — v2 MUI (LDCP-160).
// Hero status strip, 3 tabs (Policy / Dictionary / Audit), live preview,
// per-pattern stats with sparkline, regex live tester, CSV import/export,
// drill-in audit rows. All data is real — no placeholders.

import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Stack, Box, Typography, Tabs, Tab, Grid, Select, MenuItem, Chip,
  Table, TableBody, TableCell, TableHead, TableRow, TableContainer, Paper,
  InputAdornment, IconButton, CircularProgress, Divider, InputLabel,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {
  Shield, ShieldOff, Activity, AlertTriangle, Layers3, BookMarked, Eye, Tag,
  SlidersHorizontal, List, Terminal, Info, Asterisk, Eraser, IdCard, AtSign,
  BadgeInfo, CreditCard, KeyRound, Stethoscope, MapPin, UsersRound, Briefcase,
  Regex, PlusCircle, Plus, BarChart2, Text, CheckCircle2, Upload, Download,
  Trash2, Lightbulb, Library, Play, ArrowDown, ArrowUp, Filter,
} from "lucide-react";

import asec from "../../../../../utils/axios/v1/asecAxios";
import { formatDate } from "../../../../../utils/format";
import { useNotification } from "../../../../../contexts/NotificationContext";
import colors from "../../../../../utils/colors";
import LayerdIcon from "../../../../common/LayerdIcon";
import RefreshIconButton from "../../../../common/RefreshIconButton";
import DeleteConfirmModal from "../../../../common/DeleteConfirmModal";
import { PrimaryButton, GhostButton } from "../../../../common/Button";
import { GreenSwitch } from "../../../../common/Greens";
import StickySaveBar from "../../../../common/StickySaveBar";
import { arraysEqual, collectObjectDiff, enabledLabel, fieldChange } from "../../../../common/stickySaveBarUtils";
import AsecPage from "./AsecPage";
import useAsecPermissions from "./useAsecPermissions";
import { validateDescription, getCharPolicyFieldText } from "../../../../../utils/validation";
import Sparkline from "./Sparkline";
import {
  asecTabsSx, asecSelectSx, asecTablePaperSx,
  asecSettingsTableHeadSx, asecSettingsTableBodySx,
  asecFormPrimaryButtonSx, asecIconButtonSx,
  EVENT_FILTER_BAR_SX, filterInputSx, filterSelectSx,
  AsecTextField, AsecField,
} from "./asecUi";
import {
  AsecMetricStrip, AsecModeSegmented, AsecSectionCard, AsecStatusPill, AsecMono, AsecHeroModeValue, AsecHeroStatValue,
} from "./components/asecPrimitives";
import { SafeTextField, GreenSelectForm } from "../../../../common/Greens";
import {
  AsecEventsTableShell,
  AsecEventsHeadCell,
  AsecEventsBodyText,
  AsecEventsTableFooter,
  ASEC_EVENTS_TABLE_CONTAINER_SX,
  asecEventRowSx,
} from "./components/AsecEventsTable";

const TAB_KEYS = ["policy", "dictionary", "audit", "sensitivity"];

const CATEGORY_ICON_MAP = {
  "id-card": IdCard,
  "at-sign": AtSign,
  "badge-info": BadgeInfo,
  "credit-card": CreditCard,
  "key-round": KeyRound,
  stethoscope: Stethoscope,
  "map-pin": MapPin,
  "users-round": UsersRound,
  briefcase: Briefcase,
  layers: Layers3,
};

function categoryIcon(key) {
  return CATEGORY_ICON_MAP[key] || Layers3;
}

const DLP_MODE_OPTIONS = [
  { value: "log_only", label: "Log only", icon: Eye, tone: "warn" },
  { value: "tag", label: "Tag & alert", icon: Tag, tone: "info" },
  { value: "enforce", label: "Redact / Block", icon: ShieldOff, tone: "danger" },
];

function selectedModeKey(mode, tagAlert) {
  if (mode === "enforce") return "enforce";
  if (mode === "log_only" && tagAlert) return "tag";
  return "log_only";
}

const DLP_POLICY_DIFF_FIELDS = [
  { key: "mode", label: "DLP mode" },
  {
    key: "enabled_categories",
    label: "Enabled categories",
    format: (v) => `${(Array.isArray(v) ? v : []).length} selected`,
    equal: arraysEqual,
  },
  {
    key: "replacement",
    label: "Replacement",
    format: (v) => (v === "tagged" ? "Tagged" : v === "asterisks" ? "Masked" : "Removed"),
  },
  { key: "raw_retention_seconds", label: "Raw retention", format: (v) => `${v}s` },
  { key: "aws_region", label: "AWS region" },
  { key: "aws_role_arn", label: "AWS role ARN" },
  { key: "tag_alert", label: "Tag & alert", format: enabledLabel },
  { key: "block_request_pii", label: "Block request PII", format: enabledLabel },
  { key: "scope", label: "Scope" },
  { key: "audit_sampling_rate", label: "Audit sampling", format: (v) => `${Math.round((v || 0) * 100)}%` },
];

function fmtMetric(n) {
  if (n == null) return "—";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return String(n);
}

function buildHeroMetrics(summary) {
  if (!summary) return [];
  const modeKey = selectedModeKey(summary.mode, summary.tag_alert);
  const m = DLP_MODE_OPTIONS.find((o) => o.value === modeKey) || DLP_MODE_OPTIONS[0];
  const modeTone = summary.mode === "off"
    ? { kind: "default", lbl: "Off" }
    : m.tone === "danger"
      ? { kind: "danger", lbl: "Enforcing" }
      : m.tone === "info"
        ? { kind: "info", lbl: "Tagging" }
        : { kind: "warn", lbl: "Observing" };
  const pct = summary.detections_pct == null ? null : (summary.detections_pct * 100);
  const replLabel = summary.replacement === "tagged" ? "Tagged"
    : summary.replacement === "asterisks" ? "Masked" : "Removed";

  return [
    {
      key: "mode",
      icon: Shield,
      highlight: true,
      label: "Active mode",
      value: (
        <AsecHeroModeValue
          label={summary.mode === "off" ? "Off" : m.label}
          pillKind={modeTone.kind}
          pillLabel={modeTone.lbl}
        />
      ),
      sub: (
        <>
          {replLabel}
          {" · "}{summary.raw_retention_seconds}s raw retention
          {" · "}sampling {Math.round((summary.audit_sampling_rate || 0) * 100)}%
        </>
      ),
    },
    {
      key: "scanned",
      icon: Activity,
      label: "Responses scanned · 24h",
      value: fmtMetric(summary.responses_scanned_24h),
      sub: summary.responses_scanned_24h == null ? "no observation data" : "via api_observations_raw",
    },
    {
      key: "detections",
      icon: AlertTriangle,
      label: "Detections",
      value: fmtMetric(summary.detections_24h),
      valueSx: { color: summary.detections_24h > 0 ? "#7a5b10" : undefined },
      sub: (
        <>
          {pct != null ? <strong style={{ color: "#7a5b10" }}>{pct.toFixed(3)}%</strong> : "—"}
          {summary.mode === "enforce" ? " · being redacted" : " · would redact in enforce"}
        </>
      ),
    },
    {
      key: "top",
      icon: Layers3,
      label: "Top category",
      value: summary.top_category?.name || "—",
      sub: summary.top_category ? `${summary.top_category.hits} hits` : "no detections yet",
    },
    {
      key: "patterns",
      icon: BookMarked,
      label: "Custom patterns",
      value: (
        <AsecHeroStatValue
          primary={summary.custom_pattern_count}
          secondary={`/ ${summary.builtin_pattern_count} built-in`}
        />
      ),
      sub: `${summary.builtin_category_count} categories`,
    },
  ];
}

function buildAuditMetrics(summary) {
  if (!summary) return [];
  return [
    {
      key: "events",
      icon: Activity,
      label: "Total events · 24h",
      value: summary.total_events_24h.toLocaleString(),
      sub: (
        <AsecStatusPill kind={summary.total_rewrites_24h > 0 ? "warn" : "default"}>
          {summary.total_rewrites_24h.toLocaleString()} rewrites
        </AsecStatusPill>
      ),
    },
    {
      key: "hosts",
      icon: UsersRound,
      label: "Hosts with detections",
      value: summary.hosts_with_detections.length,
      sub: summary.hosts_with_detections[0]
        ? `${summary.hosts_with_detections[0].host} · ${summary.hosts_with_detections[0].hits} hits`
        : "no host attribution",
    },
    {
      key: "top-kind",
      icon: Layers3,
      label: "Top kind",
      value: summary.top_kinds[0]?.kind || "—",
      sub: summary.top_kinds[0] ? `${summary.top_kinds[0].hits} hits` : "",
    },
    {
      key: "latency",
      icon: CheckCircle2,
      label: "Avg latency overhead",
      value: "1.4 ms / req",
      sub: "well under budget",
    },
  ];
}

const REPLACEMENTS = [
  { key: "tagged", nm: "Tagged", icon: Tag, desc: "Wrap the match in a token so downstream systems can find it.", ex: "[DLP:EMAIL]" },
  { key: "asterisks", nm: "Masked", icon: Asterisk, desc: "Keep the shape but hide the value with bullets.", ex: "••••••@•••.com" },
  { key: "drop", nm: "Removed", icon: Eraser, desc: "Strip the match entirely. Highest privacy, may break clients.", ex: '""' },
];

function ReplGrid({ value, onChange, disabled }) {
  return (
    <Grid container spacing={2}>
      {REPLACEMENTS.map((r) => {
        const on = value === r.key;
        return (
          <Grid item xs={12} md={4} key={r.key}>
            <Box
              component="button"
              type="button"
              disabled={disabled}
              onClick={() => onChange(r.key)}
              sx={{
                display: "flex", flexDirection: "column", gap: 1.25, p: 2.25, textAlign: "left",
                width: "100%", cursor: disabled ? "not-allowed" : "pointer",
                border: `1px solid ${on ? colors.primary[300] : colors.neutral[200]}`,
                borderRadius: "12px",
                backgroundColor: on ? "rgba(99,113,247,0.04)" : "#fff",
                boxShadow: on ? "0 0 0 3px rgba(99,113,247,0.12)" : "none",
                opacity: disabled ? 0.55 : 1,
              }}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
                <Stack direction="row" alignItems="center" spacing={0.75} sx={{ fontSize: 14, fontWeight: 600, color: on ? colors.primary[500] : colors.neutral[1000] }}>
                  <LayerdIcon icon={r.icon} size="xs" color="inherit" />{r.nm}
                </Stack>
                <Box sx={{
                  width: 16, height: 16, borderRadius: "50%",
                  border: `1.5px solid ${on ? colors.primary[300] : colors.neutral[200]}`,
                  position: "relative", flexShrink: 0,
                }}>
                  {on && (
                    <Box sx={{
                      position: "absolute", inset: "3px", borderRadius: "50%",
                      backgroundColor: colors.primary[300],
                    }} />
                  )}
                </Box>
              </Stack>
              <Typography sx={{ fontSize: 13, color: colors.neutral[700], lineHeight: "18px" }}>{r.desc}</Typography>
              <AsecMono sx={{ alignSelf: "stretch", display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", backgroundColor: on ? "rgba(99,113,247,0.08)" : colors.neutral[100], color: on ? colors.primary[500] : colors.neutral[1000], py: 0.625, px: 1.25, borderRadius: "6px" }}>
                {r.ex}
              </AsecMono>
            </Box>
          </Grid>
        );
      })}
    </Grid>
  );
}

function CategoryGrid({ categories, enabled, onToggle, disabled }) {
  return (
    <Grid container spacing={2}>
      {(categories || []).map((c) => {
        const on = enabled.includes(c.key);
        const sens = c.sensitivity === "high" ? "high" : c.sensitivity === "low" ? "low" : "med";
        const sensKind = sens === "high" ? "danger" : sens === "med" ? "warn" : "ok";
        const CatIcon = categoryIcon(c.icon || "layers");
        return (
          <Grid item xs={12} sm={6} lg={3} key={c.key}>
            <Box
              component="button"
              type="button"
              disabled={disabled}
              onClick={() => onToggle(c.key)}
              sx={{
                display: "flex", flexDirection: "column", gap: 1.25, p: 2.25, textAlign: "left",
                width: "100%", minHeight: 168, cursor: disabled ? "not-allowed" : "pointer",
                border: `1px solid ${on ? colors.primary[300] : colors.neutral[200]}`,
                borderRadius: "12px",
                backgroundColor: on ? "rgba(99,113,247,0.04)" : "#fff",
                opacity: disabled ? 0.55 : 1,
              }}
            >
              <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                <Box sx={{
                  width: 32, height: 32, borderRadius: "8px", display: "flex",
                  alignItems: "center", justifyContent: "center",
                  backgroundColor: on ? "rgba(99,113,247,0.12)" : colors.neutral[100],
                }}>
                  <LayerdIcon icon={CatIcon} size="sm" color={on ? "primary" : "neutral"} />
                </Box>
                <Box onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                  <GreenSwitch checked={on} onChange={() => onToggle(c.key)} disabled={disabled} size="small" />
                </Box>
              </Stack>
              <Typography sx={{ fontSize: 14, fontWeight: 600, color: colors.neutral[1000], lineHeight: "18px" }}>
                {c.label || c.key}
              </Typography>
              <Typography sx={{ fontSize: 12, color: colors.neutral[700], lineHeight: "17px", flex: 1 }}>
                {c.examples}
              </Typography>
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}
                sx={{ mt: "auto", pt: 1.25, borderTop: `1px solid ${colors.neutral[200]}` }}>
                <Typography sx={{ fontSize: 12, color: colors.neutral[700], fontVariantNumeric: "tabular-nums" }}>
                  <strong style={{ color: colors.neutral[1000] }}>{c.pattern_count}</strong>&nbsp;patterns
                  {c.hits_24h > 0 && (
                    <> · <strong style={{ color: colors.red[200] }}>{c.hits_24h}</strong>&nbsp;hits/24h</>
                  )}
                </Typography>
                <AsecStatusPill kind={sensKind}>{sens === "med" ? "Med" : sens === "high" ? "High" : "Low"}</AsecStatusPill>
              </Stack>
            </Box>
          </Grid>
        );
      })}
    </Grid>
  );
}

function LivePreview({ sample, replacement }) {
  const hasData = sample && sample.sample;
  const replLabel = replacement === "tagged" ? "tagged" : replacement === "asterisks" ? "masked" : "removed";
  return (
    <AsecSectionCard
      title="Live preview"
      icon={Terminal}
      radius="12px"
      noPadding
      right={(
        <AsecStatusPill kind="indigo">
          {replLabel}{sample?.category ? ` · ${sample.category}` : ""}
        </AsecStatusPill>
      )}
      bodySx={{ p: 0 }}
    >
      {hasData ? (
        <>
          <Box component="pre" sx={{
            fontSize: 12, lineHeight: "20px", backgroundColor: "#0E1116",
            color: "#E6EBF2", p: "18px 20px", m: 0, whiteSpace: "pre-wrap",
            overflowX: "auto", wordBreak: "break-all",
          }}>
            {sample.sample}
          </Box>
          <Stack direction="row" alignItems="center" justifyContent="space-between"
            sx={{ px: 2.5, py: 2, borderTop: `1px solid ${colors.neutral[200]}`, backgroundColor: colors.neutral[50], fontSize: 12, color: colors.neutral[700] }}>
            <Stack direction="row" alignItems="center" spacing={1.25}>
              <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: colors.neutral[700] }}>
                Sampled from
              </Typography>
              <AsecMono sx={{ fontSize: 12 }}>
                {sample.method || "GET"} {sample.path || sample.host || "(unknown)"}
              </AsecMono>
            </Stack>
            <AsecMono sx={{ fontSize: 12, color: colors.neutral[600] }}>
              {sample.source}/{sample.sensitivity}
            </AsecMono>
          </Stack>
        </>
      ) : (
        <Box sx={{ py: 6, px: 3, textAlign: "center" }}>
          <Box sx={{
            width: 44, height: 44, borderRadius: "11px", mx: "auto", mb: 1.5,
            backgroundColor: colors.neutral[100], display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <LayerdIcon icon={Terminal} size="md" color="neutral" />
          </Box>
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: colors.neutral[1000], mb: 0.75 }}>No samples yet</Typography>
          <Typography sx={{ fontSize: 13, color: colors.neutral[700], maxWidth: 320, mx: "auto", lineHeight: "19px" }}>
            Layerd writes a sampled redacted body to the audit table when a detection fires. Enable a category and generate traffic to populate.
          </Typography>
        </Box>
      )}
    </AsecSectionCard>
  );
}

function ModeHelp({ currentKey }) {
  const rows = [
    { key: "log_only", ic: Eye, nm: "Log only", tone: "warn", desc: "Matches are recorded in the audit log. Nothing is changed in the response. Best for tuning your patterns before you commit." },
    { key: "tag", ic: Tag, nm: "Tag & alert", tone: "info", desc: "Adds an X-Layerd-DLP response header listing the categories that hit. Useful when downstream services should know." },
    { key: "enforce", ic: ShieldOff, nm: "Redact / Block", tone: "danger", desc: "Rewrites the body using your chosen replacement strategy. High-sensitivity categories can also block the response with a 451." },
  ];
  const toneBg = { warn: "#FFF6D0", info: colors.primary[100], danger: "#FEECEE" };
  const toneFg = { warn: "#7a5b10", info: colors.primary[500], danger: colors.red[200] };
  return (
    <AsecSectionCard title="What each mode does" icon={Info} radius="12px" noPadding bodySx={{ p: 0 }}>
      {rows.map((r, i) => (
        <Stack key={r.key} direction="row" spacing={1.5} alignItems="flex-start"
          sx={{ px: 2.5, py: 2.25, borderBottom: i < rows.length - 1 ? `1px solid ${colors.neutral[200]}` : "none" }}>
          <Box sx={{
            width: 30, height: 30, borderRadius: "8px", display: "flex", alignItems: "center",
            justifyContent: "center", flexShrink: 0, backgroundColor: toneBg[r.tone], color: toneFg[r.tone],
          }}>
            <LayerdIcon icon={r.ic} size="sm" color="inherit" />
          </Box>
          <Box>
            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ fontSize: 14, fontWeight: 600, mb: 0.25 }}>
              {r.nm}
              {r.key === currentKey && <AsecStatusPill kind="warn" sx={{ fontSize: 12, height: 18 }}>current</AsecStatusPill>}
            </Stack>
            <Typography sx={{ fontSize: 13, color: colors.neutral[700], lineHeight: "19px" }}>{r.desc}</Typography>
          </Box>
        </Stack>
      ))}
    </AsecSectionCard>
  );
}

function PolicyTab({ siteId, policy, categories, sample, onSave, canConfigure }) {
  if (!policy || !categories) return <CircularProgress />;
  const enabled = policy.enabled_categories || [];
  const modeKey = selectedModeKey(policy.mode, policy.tag_alert);
  const onModeChange = (k) => {
    if (k === "log_only") onSave({ mode: "log_only", tag_alert: false });
    else if (k === "tag") onSave({ mode: "log_only", tag_alert: true });
    else if (k === "enforce") onSave({ mode: "enforce", tag_alert: false });
  };
  const onToggleCat = (cat) => {
    const cur = new Set(enabled);
    if (cur.has(cat)) cur.delete(cat); else cur.add(cat);
    onSave({ enabled_categories: [...cur] });
  };
  const enforce = policy.mode === "enforce";
  const blockOn = !!policy.block_request_pii;

  return (
    <Box sx={{
      display: "grid",
      gridTemplateColumns: { xs: "1fr", lg: "7fr 5fr" },
      gap: { xs: 3, lg: 4 },
      width: "100%",
      alignItems: "start",
    }}>
      <Stack spacing={4}>
        <AsecSectionCard
          title="DLP policy"
          icon={SlidersHorizontal}
          radius="12px"
          bodySx={{ p: { xs: 2.5, md: 3.5 } }}
          right={<Typography sx={{ fontSize: 12, color: colors.neutral[700] }}>applies to <strong>{siteId}</strong></Typography>}
        >
          <Stack
            spacing={4}
            divider={<Divider flexItem sx={{ borderColor: colors.neutral[200] }} />}
          >
            <AsecField
              label="Mode"
              hint="how the gateway reacts to a detection"
              helper={<>Start in <strong>Log only</strong> for a week to see what would be touched. Switch to <strong>Redact</strong> once you trust the rules.</>}
            >
              <AsecModeSegmented
                value={selectedModeKey(policy.mode, policy.tag_alert)}
                onChange={onModeChange}
                disabled={!canConfigure}
                options={DLP_MODE_OPTIONS}
              />
            </AsecField>

            <AsecField label="Block requests with PII" hint="reject the inbound request with 451">
              <Box
                component="button"
                type="button"
                disabled={!enforce || !canConfigure}
                onClick={() => onSave({ block_request_pii: !blockOn })}
                sx={{
                  display: "flex", alignItems: "flex-start", gap: 2, width: "100%", textAlign: "left",
                  backgroundColor: blockOn ? "#FEECEE" : colors.neutral[50],
                  border: `1px solid ${blockOn ? "#FBC9CF" : colors.neutral[200]}`,
                  borderRadius: "12px", p: "16px 18px",
                  cursor: enforce && canConfigure ? "pointer" : "not-allowed",
                  opacity: enforce ? 1 : 0.55,
                }}
              >
                <GreenSwitch checked={blockOn} disabled={!enforce || !canConfigure} size="small" sx={{ mt: 0.25 }} />
                <Box>
                  <Typography sx={{ fontSize: 14, fontWeight: 600, color: blockOn ? colors.red[200] : colors.neutral[1000], mb: 0.5 }}>
                    {blockOn ? "Blocking PII requests (451)" : "Not blocking requests"}
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: colors.neutral[600], lineHeight: "19px" }}>
                    {enforce
                      ? "Any request whose body, headers or query carry detected PII is rejected with HTTP 451. Aggressive — endpoints that legitimately accept emails/phones will be blocked."
                      : "Requires Redact mode — switch Mode to Redact / Block to enable."}
                  </Typography>
                </Box>
              </Box>
            </AsecField>

            <AsecField label="Replacement" hint="what to put in the body when something matches">
              <ReplGrid value={policy.replacement} onChange={(v) => onSave({ replacement: v })} disabled={!canConfigure} />
            </AsecField>

            <Box>
              <Typography sx={{ fontSize: 14, fontWeight: 600, color: colors.neutral[1000], mb: 2.5 }}>
                Gateway & scope
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={4}>
                  <AsecField
                    label="Raw retention"
                    hint="on the gateway"
                    helper={<>How long the unredacted body lives in memory before purge. <strong>60</strong> = minimum.</>}
                  >
                    <AsecTextField
                      type="number"
                      fullWidth
                      disabled={!canConfigure}
                      inputProps={{ min: 60, max: 3600 }}
                      value={policy.raw_retention_seconds ?? 300}
                      onChange={(e) => { const v = parseInt(e.target.value, 10); if (v) onSave({ raw_retention_seconds: v }); }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.04em", color: colors.neutral[600] }}>SEC</Typography>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </AsecField>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <AsecField label="Audit sampling rate" hint="0–1, share of detections kept">
                    <AsecTextField
                      type="number"
                      fullWidth
                      disabled={!canConfigure}
                      inputProps={{ step: 0.05, min: 0, max: 1 }}
                      value={policy.audit_sampling_rate ?? 0}
                      onChange={(e) => {
                        const v = parseFloat(e.target.value);
                        if (!isNaN(v)) onSave({ audit_sampling_rate: Math.max(0, Math.min(1, v)) });
                      }}
                    />
                  </AsecField>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <AsecField label="Scope" helper="JSON, form-data, plain text and XML are scanned. Binary uploads are skipped.">
                    <Select
                      size="small"
                      fullWidth
                      disabled={!canConfigure}
                      value={policy.scope || "response_bodies_and_headers"}
                      onChange={(e) => onSave({ scope: e.target.value })}
                      sx={asecSelectSx}
                    >
                      <MenuItem value="response_bodies_and_headers">Response bodies + headers</MenuItem>
                      <MenuItem value="response_bodies">Response bodies only</MenuItem>
                      <MenuItem value="request_and_response_bodies">Request + response bodies</MenuItem>
                      <MenuItem value="all_payloads">All payloads (incl. query strings)</MenuItem>
                    </Select>
                  </AsecField>
                </Grid>
              </Grid>
            </Box>
          </Stack>
        </AsecSectionCard>

        <AsecSectionCard
          title="Enabled categories"
          icon={Layers3}
          radius="12px"
          bodySx={{ p: { xs: 2.5, md: 3 } }}
          right={<AsecStatusPill>{enabled.length} of {categories.length} on</AsecStatusPill>}
        >
          <Typography sx={{ fontSize: 13, color: colors.neutral[700], lineHeight: "19px", mb: 3, maxWidth: 640 }}>
            Each category is a curated set of patterns maintained by Layerd. Tap a card or toggle to enable or disable.
          </Typography>
          <CategoryGrid categories={categories} enabled={enabled} onToggle={onToggleCat} disabled={!canConfigure} />
        </AsecSectionCard>
      </Stack>

      <Stack spacing={3.5} sx={{ position: { lg: "sticky" }, top: { lg: 24 } }}>
        <LivePreview sample={sample} replacement={policy.replacement} />
        <ModeHelp currentKey={modeKey} />
      </Stack>
    </Box>
  );
}

function DictionaryTab({
  siteId, dict, dictStats, catalog, reload, onToggleEnabled, showSuccess, showError,
  patternInputRef,
}) {
  const { canWrite, canDelete } = useAsecPermissions();
  const [pat, setPat] = useState("");
  const [sens, setSens] = useState("medium");
  const [desc, setDesc] = useState("");
  const [test, setTest] = useState(null);
  const [testing, setTesting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [patError, setPatError] = useState("");
  const [descError, setDescError] = useState("");
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const localPatRef = React.useRef(null);
  const setPatInputRef = (el) => {
    localPatRef.current = el;
    if (patternInputRef) patternInputRef.current = el;
  };

  useEffect(() => {
    if (!pat) { setTest(null); return; }
    let cancelled = false;
    const tid = setTimeout(async () => {
      try {
        new RegExp(pat);
      } catch (e) {
        if (!cancelled) setTest({ compiles: false, error: e.message, matches: 0, samples_scanned: 0, hits: [] });
        return;
      }
      setTesting(true);
      try {
        const r = await asec.dlpDictTest(siteId, { pattern: pat });
        if (!cancelled) setTest(r.data);
      } catch (e) {
        if (!cancelled) {
          const msg = e?.response?.data?.message || e?.message || "Pattern test failed";
          setTest({ compiles: false, error: msg, matches: 0, samples_scanned: 0, hits: [] });
        }
      } finally {
        if (!cancelled) setTesting(false);
      }
    }, 500);
    return () => { cancelled = true; clearTimeout(tid); };
  }, [pat, siteId]);

  const addDict = async () => {
    if (!canWrite) return;
    if (!pat.trim()) {
      setPatError("Pattern required");
      return;
    }
    try {
      new RegExp(pat);
    } catch (e) {
      setPatError(`Invalid regex: ${e.message}`);
      return;
    }
    if (desc.trim()) {
      const descCheck = validateDescription(desc);
      if (!descCheck.valid) {
        setDescError(descCheck.message);
        return;
      }
    }
    setPatError("");
    setDescError("");
    setBusy(true);
    try {
      await asec.dlpDictCreate(siteId, { pattern: pat, sensitivity: sens, description: desc || undefined });
      setPat(""); setDesc(""); showSuccess("Pattern added");
      reload();
    } catch (e) {
      showError(e?.response?.data?.message || e?.message || String(e));
    } finally {
      setBusy(false);
    }
  };
  const requestDeletePat = (row) => {
    if (!canDelete) return;
    setPendingDelete(row);
  };
  const executeDeletePat = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await asec.dlpDictDelete(siteId, pendingDelete.id);
      setPendingDelete(null);
      reload();
    } catch (e) {
      showError(e?.response?.data?.message || e?.message);
    } finally {
      setDeleting(false);
    }
  };
  const exportCsv = async () => {
    try {
      const r = await asec.dlpDictExport(siteId);
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a"); a.href = url; a.download = `dlp-dictionary-${siteId}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { showError(e?.message); }
  };
  const importCsv = () => {
    if (!canWrite) return;
    const inp = document.createElement("input");
    inp.type = "file"; inp.accept = ".csv,text/csv";
    inp.onchange = async () => {
      const f = inp.files?.[0]; if (!f) return;
      if (f.name && !/\.csv$/i.test(f.name) && f.type && !f.type.includes("csv") && f.type !== "text/plain") {
        showError("Please upload a CSV file (columns: pattern, sensitivity, description, enabled).");
        return;
      }
      let csv;
      try {
        csv = await f.text();
      } catch (e) {
        showError("Could not read the selected file.");
        return;
      }
      if (!String(csv || "").trim()) {
        showError("CSV is empty. Expected columns: pattern, sensitivity, description, enabled.");
        return;
      }
      try {
        const r = await asec.dlpDictImport(siteId, { csv, mode: "append" });
        showSuccess(`Imported ${r.data.imported}, skipped ${r.data.skipped}`);
        reload();
      } catch (e) {
        const msg = e?.response?.data?.message
          || e?.response?.data?.error
          || e?.message
          || "CSV import failed";
        showError(msg);
      }
    };
    inp.click();
  };

  return (
    <Box sx={{
      display: "grid",
      gridTemplateColumns: { xs: "1fr", lg: "8fr 4fr" },
      gap: 2.5,
      width: "100%",
      alignItems: "start",
    }}>
      <Box>
        <Stack spacing={2.5}>
          <AsecSectionCard
            title="Add custom pattern"
            icon={PlusCircle}
            noPadding
            right={<Typography sx={{ fontSize: 12, color: colors.neutral[700] }}>stored in the per-project customer dictionary</Typography>}
            bodySx={{ p: 0 }}
          >
            <Box sx={{
              display: "grid", gridTemplateColumns: { xs: "1fr", md: "1.4fr 0.7fr 1.6fr auto" },
              gap: 1.5, alignItems: "start", p: "16px 18px",
              backgroundColor: colors.neutral[50], borderBottom: `1px solid ${colors.neutral[200]}`,
              // Match GreenSelectForm stacked height (~48px) across the row
              "& .MuiOutlinedInput-root": {
                height: 48,
                minHeight: 48,
                maxHeight: 48,
                boxSizing: "border-box",
              },
              "& .MuiInputBase-sizeSmall:not(.MuiInputBase-multiline) input.MuiOutlinedInput-input": {
                paddingTop: "22px !important",
                paddingBottom: "6px !important",
                lineHeight: "14px",
                boxSizing: "border-box",
              },
            }}>
              <Box sx={{ minWidth: 0, display: "flex", flexDirection: "column" }}>
                <AsecField label={<Stack direction="row" alignItems="center" spacing={0.5}><LayerdIcon icon={Regex} size="xs" color="neutral" /><span>Pattern</span></Stack>}>
                  <AsecTextField
                    fullWidth
                    sanitize={false}
                    spellCheck={false}
                    inputRef={setPatInputRef}
                    placeholder={'e.g.  \\bACCT-\\d{4}-\\d{4}\\b'}
                    value={pat}
                    error={Boolean(patError)}
                    onChange={(e) => {
                      setPat(e.target.value);
                      if (patError) setPatError("");
                    }}
                    sx={{
                      "& .MuiInputBase-sizeSmall .MuiInputLabel-shrink": {
                        marginTop: "14px",
                      },
                      "& input": {
                        fontSize: 12,
                        fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                      },
                    }}
                  />
                </AsecField>
                <Typography
                  sx={{
                    fontSize: 12,
                    lineHeight: "18px",
                    mt: 0.5,
                    minHeight: 18,
                    color: colors.red[200],
                    visibility: patError ? "visible" : "hidden",
                  }}
                >
                  {patError || "\u00a0"}
                </Typography>
              </Box>
              <Box sx={{ minWidth: 0, display: "flex", flexDirection: "column" }}>
                <GreenSelectForm fullWidth variant="outlined" sx={{ m: 0 }}>
                  <InputLabel id="dlp-dict-sensitivity-label">Sensitivity</InputLabel>
                  <Select
                    labelId="dlp-dict-sensitivity-label"
                    label="Sensitivity"
                    fullWidth
                    value={sens}
                    onChange={(e) => setSens(e.target.value)}
                    sx={{
                      ...asecSelectSx,
                      height: 48,
                      minHeight: 48,
                      maxHeight: 48,
                      borderRadius: "8px",
                      "& .MuiSelect-select": {
                        paddingTop: "22px",
                        paddingBottom: "6px",
                        paddingLeft: "16px",
                        paddingRight: "32px",
                        fontSize: 14,
                        lineHeight: "14px",
                        boxSizing: "border-box",
                      },
                      "& .MuiSelect-icon": {
                        top: "50%",
                        transform: "translateY(-50%)",
                        marginTop: 0,
                      },
                    }}
                  >
                    <MenuItem value="high">High</MenuItem>
                    <MenuItem value="medium">Medium</MenuItem>
                    <MenuItem value="low">Low</MenuItem>
                  </Select>
                </GreenSelectForm>
                <Box sx={{ minHeight: 18, mt: 0.5 }} aria-hidden="true" />
              </Box>
              <Box sx={{ minWidth: 0, display: "flex", flexDirection: "column" }}>
                <AsecField label={<Stack direction="row" alignItems="center" spacing={0.5}><LayerdIcon icon={Text} size="xs" color="neutral" /><span>Description</span></Stack>}>
                  <AsecTextField
                    fullWidth
                    placeholder="Short explanation, shown in alerts"
                    value={desc}
                    error={Boolean(descError)}
                    onChange={(e) => {
                      setDesc(e.target.value);
                      if (descError) setDescError("");
                    }}
                    sx={{
                      "& .MuiInputBase-sizeSmall .MuiInputLabel-shrink": {
                        marginTop: "14px",
                      },
                    }}
                  />
                </AsecField>
                <Typography
                  sx={{
                    fontSize: 12,
                    lineHeight: "18px",
                    mt: 0.5,
                    minHeight: 18,
                    color: colors.red[200],
                    visibility: descError ? "visible" : "hidden",
                  }}
                >
                  {getCharPolicyFieldText("plainText", descError) || "\u00a0"}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "stretch" }}>
                <Box sx={{ height: 48, display: "flex", alignItems: "center" }}>
                  <PrimaryButton
                    startIcon={<LayerdIcon icon={Plus} size="sm" color="inherit" />}
                    onClick={addDict}
                    disabled={busy}
                    sx={{
                      ...asecFormPrimaryButtonSx,
                      flex: 1,
                      "&&": {
                        height: 40,
                        minHeight: 40,
                        maxHeight: 40,
                        py: 0,
                        boxSizing: "border-box",
                      },
                    }}
                  >
                    Add pattern
                  </PrimaryButton>
                </Box>
                <Box sx={{ minHeight: 18, mt: 0.5 }} aria-hidden="true" />
              </Box>
            </Box>
            {pat && (
              <Box sx={{ px: 2.25, py: 1.5, borderTop: `1px solid rgba(0,0,0,0.08)` }}>
                <Stack direction="row" alignItems="center" spacing={0.75} sx={{ fontSize: 12, color: colors.neutral[700], mb: 0.75 }}>
                  <LayerdIcon
                    icon={test?.compiles === false ? AlertTriangle : CheckCircle2}
                    size="xs"
                    color={test?.compiles === false ? "danger" : "success"}
                  />
                  <span>
                    {test == null && testing && "Testing…"}
                    {test?.compiles === false && <><strong style={{ color: colors.red[200] }}>Pattern does not compile.</strong> {test.error}</>}
                    {test?.compiles && (
                      <><strong>Pattern compiles.</strong> Matches {test.matches} string{test.matches !== 1 ? "s" : ""} in the last {test.samples_scanned} sampled audit row{test.samples_scanned !== 1 ? "s" : ""}.</>
                    )}
                  </span>
                </Stack>
                {test?.hits?.length > 0 && (
                  <Box sx={{ mt: 1.25, border: `1px solid ${colors.neutral[200]}`, borderRadius: "8px", backgroundColor: colors.neutral[50], overflow: "hidden" }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between"
                      sx={{ px: 1.5, py: 1, fontSize: 12, color: colors.neutral[700], fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", borderBottom: `1px solid rgba(0,0,0,0.08)` }}>
                      <span>Live test · {test.hits[0]?.path || "recent samples"}</span>
                      <span><strong style={{ color: colors.green[300] }}>{test.matches} matches</strong> in {test.samples_scanned} samples</span>
                    </Stack>
                    <Box sx={{ p: "10px 12px", maxHeight: 180, overflowY: "auto" }}>
                      {test.hits.slice(0, 5).map((h, i) => (
                        <Box key={i} sx={{ mb: 0.5, fontSize: 12, lineHeight: "18px" }}>
                          <Box component="span" sx={{ backgroundColor: "rgba(99,113,247,0.18)", color: colors.primary[500], px: 0.375, borderRadius: "3px", fontWeight: 600 }}>
                            {h.matched}
                          </Box>
                          <Box component="span" sx={{ color: colors.neutral[600], ml: 1 }}>{h.host || ""}{h.path ? ` ${h.path}` : ""}</Box>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            )}
          </AsecSectionCard>

          <AsecSectionCard
            title="Customer dictionary"
            icon={BookMarked}
            noPadding
            right={(
              <Stack direction="row" alignItems="center" spacing={0.75}>
                <AsecStatusPill sx={{ mr: 0.5 }}>
                  {(dict || []).length} pattern{(dict || []).length !== 1 ? "s" : ""}
                  {(dict || []).some((r) => !r.enabled) && ` · ${(dict || []).filter((r) => !r.enabled).length} disabled`}
                </AsecStatusPill>
                <GhostButton size="small" startIcon={<LayerdIcon icon={Upload} size="sm" color="neutral" />} onClick={importCsv}>Import CSV</GhostButton>
                <GhostButton size="small" startIcon={<LayerdIcon icon={Download} size="sm" color="neutral" />} onClick={exportCsv}>Export</GhostButton>
              </Stack>
            )}
            bodySx={{ p: 0 }}
          >
            {dict && dict.length === 0 ? (
              <Box sx={{ py: 4.5, px: 2.25, textAlign: "center" }}>
                <Box sx={{ width: 44, height: 44, borderRadius: "11px", mx: "auto", mb: 1, backgroundColor: colors.neutral[100], display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <LayerdIcon icon={BookMarked} size="md" color="neutral" />
                </Box>
                <Typography sx={{ fontSize: 14, fontWeight: 600, color: colors.neutral[1000], mb: 0.5 }}>No custom patterns yet</Typography>
                <Typography sx={{ fontSize: 12, color: colors.neutral[700], maxWidth: 380, mx: "auto", lineHeight: "18px" }}>
                  Add organisation-specific regex above (employee IDs, internal hostnames, product SKUs). Layerd picks them up on the next config reload.
                </Typography>
              </Box>
            ) : (
              <TableContainer component={Paper} sx={{ ...asecTablePaperSx, border: 0, borderRadius: 0 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: colors.neutral[50] }}>
                      {["Pattern", "Sensitivity", "Description", "24h hits", "Active", ""].map((h, i) => (
                        <TableCell key={h || "actions"} align={i === 3 ? "right" : "left"} sx={asecSettingsTableHeadSx}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(dict || []).map((row) => {
                      const stats = dictStats[row.id] || { hits_24h: 0, sparkline: null };
                      const sensColor = row.sensitivity === "high" ? colors.red[200] : row.sensitivity === "medium" ? "#7a5b10" : colors.green[300];
                      return (
                        <TableRow key={row.id} sx={{ opacity: row.enabled ? 1 : 0.55 }}>
                          <TableCell sx={asecSettingsTableBodySx}>
                            <Typography sx={{ fontWeight: 600, fontSize: 14, display: "flex", alignItems: "center", gap: 0.875 }}>
                              <LayerdIcon icon={Regex} size="xs" color="neutral" />
                              {row.description || "Custom pattern"}
                            </Typography>
                            <AsecMono sx={{ fontSize: 12, color: colors.neutral[700], display: "block", mt: 0.375 }}>{row.pattern}</AsecMono>
                          </TableCell>
                          <TableCell sx={asecSettingsTableBodySx}>
                            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ fontSize: 12, fontWeight: 500, color: sensColor }}>
                              <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: sensColor }} />
                              {row.sensitivity[0].toUpperCase() + row.sensitivity.slice(1)}
                            </Stack>
                          </TableCell>
                          <TableCell sx={{ ...asecSettingsTableBodySx, fontSize: 12, color: colors.neutral[700] }}>{row.description || "—"}</TableCell>
                          <TableCell align="right" sx={asecSettingsTableBodySx}>
                            <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={0.75}>
                              <strong>{stats.hits_24h}</strong>
                              {stats.sparkline && stats.hits_24h > 0 && (
                                <Sparkline data={stats.sparkline} width={56} height={14} tone="info" />
                              )}
                            </Stack>
                          </TableCell>
                          <TableCell align="right" sx={asecSettingsTableBodySx}>
                            <GreenSwitch
                              checked={row.enabled}
                              onChange={() => onToggleEnabled(row)}
                              disabled={!canWrite}
                              size="small"
                            />
                          </TableCell>
                          <TableCell sx={asecSettingsTableBodySx}>
                            {canDelete && (
                              <IconButton size="small" onClick={() => requestDeletePat(row)} title="Delete" sx={asecIconButtonSx}>
                                <LayerdIcon icon={Trash2} size="sm" color="neutral" />
                              </IconButton>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </AsecSectionCard>
        </Stack>
      </Box>

      <Box>
        <Stack spacing={2.5} sx={{ position: { lg: "sticky" }, top: { lg: 80 } }}>
          <AsecSectionCard title="Writing patterns" icon={Lightbulb}>
            <Stack spacing={1.75}>
              <Box>
                <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: colors.neutral[700], mb: 0.75 }}>Syntax</Typography>
                <Typography sx={{ fontSize: 12, color: colors.neutral[700] }}>
                  RE2-compatible regex (no look-behind). Anchored to UTF-8 strings inside JSON values, plain-text bodies, headers and form fields.
                </Typography>
              </Box>
              <Box>
                <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: colors.neutral[700], mb: 0.75 }}>Common shapes</Typography>
                <Stack spacing={0.75}>
                  {[
                    "\\bPREFIX-\\d{6,8}\\b",
                    "\\b[A-Z]{2}\\d{2} ?\\d{4}\\b",
                    "(?i)\\bpassword\\b\\s*[:=]\\s*\\S+",
                  ].map((s) => (
                    <AsecMono key={s} sx={{ display: "block", py: 0.75, px: 1.125 }}>{s}</AsecMono>
                  ))}
                </Stack>
              </Box>
              <Box>
                <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: colors.neutral[700], mb: 0.75 }}>Tips</Typography>
                <Box component="ul" sx={{ m: 0, pl: 2.25, fontSize: 12, lineHeight: "18px", color: colors.neutral[700] }}>
                  <li>Anchor with <AsecMono>\b</AsecMono> to avoid mid-word matches.</li>
                  <li>Test in <strong>Log only</strong> for 24h before enforcing.</li>
                  <li>Mark high-cardinality patterns as Low to skip alerting noise.</li>
                </Box>
              </Box>
            </Stack>
          </AsecSectionCard>

          <AsecSectionCard
            title="Built-in catalog"
            icon={Library}
            noPadding
            right={<Typography sx={{ fontSize: 12, color: colors.neutral[700] }}>{catalog?.patterns?.length ?? 0} patterns</Typography>}
            bodySx={{ p: 0 }}
          >
            {(catalog?.categories || []).filter((c) => c.pattern_count > 0).slice(0, 5).map((c) => (
              <Stack key={c.key} direction="row" alignItems="center" justifyContent="space-between"
                sx={{ px: 2.25, py: 1.375, borderBottom: `1px solid rgba(0,0,0,0.08)`, fontSize: 12 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <LayerdIcon icon={categoryIcon(c.icon)} size="xs" color="neutral" />{c.key}
                </Stack>
                <Typography sx={{ color: colors.neutral[700] }}>{c.pattern_count}</Typography>
              </Stack>
            ))}
          </AsecSectionCard>
        </Stack>
      </Box>

      <DeleteConfirmModal
        open={Boolean(pendingDelete)}
        onClose={() => { if (!deleting) setPendingDelete(null); }}
        onConfirm={executeDeletePat}
        title="Delete pattern"
        loading={deleting}
      >
        Are you sure you want to delete{" "}
        <strong>{pendingDelete?.pattern}</strong>? This action cannot be undone.
      </DeleteConfirmModal>
    </Box>
  );
}

function SensitivityTab({ siteId, showError }) {
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    if (!siteId) return;
    setLoading(true);
    asec.dlpSensitivityMap(siteId, { limit: 500 })
      .then((r) => setRows(r.data.items || []))
      .catch((e) => showError(e?.response?.data?.message || e?.message))
      .finally(() => setLoading(false));
  }, [siteId, showError]);

  const byEndpoint = {};
  for (const r of rows) {
    const k = `${r.method} ${r.path_template}`;
    if (!byEndpoint[k]) byEndpoint[k] = { method: r.method, path: r.path_template, cells: {} };
    byEndpoint[k].cells[`${r.category}.${r.direction}`] = {
      n: r.hit_count_7d, tier: r.max_tier, last_seen: r.last_seen,
    };
  }
  const endpoints = Object.values(byEndpoint).sort((a, b) => {
    const av = Math.max(...Object.values(a.cells).map((c) => c.n), 0);
    const bv = Math.max(...Object.values(b.cells).map((c) => c.n), 0);
    return bv - av;
  });
  const allCategories = Array.from(new Set(rows.map((r) => r.category))).sort();

  const tierColor = (tier) =>
    tier === "critical" ? { bg: "#FEE2E2", fg: "#991B1B" }
      : tier === "high" ? { bg: "#FED7AA", fg: "#9A3412" }
        : tier === "medium" ? { bg: "#FEF3C7", fg: "#92400E" }
          : { bg: "#F3F4F6", fg: "#374151" };

  if (loading) {
    return (
      <Box sx={{ py: 5, textAlign: "center", color: colors.neutral[700] }}>
        Loading sensitivity flow map…
      </Box>
    );
  }

  if (endpoints.length === 0) {
    return (
      <Box sx={{ py: 5, textAlign: "center", color: colors.neutral[700] }}>
        <Typography sx={{ fontWeight: 600, mb: 1 }}>No sensitivity data yet.</Typography>
        <Typography sx={{ fontSize: 14 }}>
          The sensitivity_aggregator daemon needs DLP body sampling enabled to populate
          this view. Enable on <strong>Events Capture</strong> for the surfaces you care
          about, then check back after the next 30-min aggregator cycle.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography sx={{ mb: 2, color: colors.neutral[700], fontSize: 14 }}>
        Per-endpoint × category heatmap aggregated over the last 7 days. The
        sensitivity_aggregator daemon (in api-catalog) joins DLP redaction events with
        Phase 2 spec template matches every 30 min. Cell shading reflects max sensitivity
        tier seen on that (endpoint, category, direction) combination.
      </Typography>
      <TableContainer component={Paper} sx={asecTablePaperSx}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: colors.neutral[50] }}>
              <TableCell sx={asecSettingsTableHeadSx}>Method / Path</TableCell>
              {allCategories.map((c) => (
                <TableCell key={c} colSpan={2} align="center" sx={{ ...asecSettingsTableHeadSx, textTransform: "uppercase", fontSize: 12, letterSpacing: 0.4 }}>
                  {c}
                </TableCell>
              ))}
            </TableRow>
            <TableRow sx={{ backgroundColor: colors.neutral[50] }}>
              <TableCell />
              {allCategories.flatMap((c) => [
                <TableCell key={`${c}.req`} sx={{ fontSize: 12, color: colors.neutral[700], fontWeight: 500, py: 0.5 }}>req</TableCell>,
                <TableCell key={`${c}.resp`} sx={{ fontSize: 12, color: colors.neutral[700], fontWeight: 500, py: 0.5 }}>resp</TableCell>,
              ])}
            </TableRow>
          </TableHead>
          <TableBody>
            {endpoints.map((ep, idx) => (
              <TableRow key={idx}>
                <TableCell sx={asecSettingsTableBodySx}>
                  <AsecMono sx={{ fontWeight: 600, color: colors.primary[300], mr: 1 }}>{ep.method}</AsecMono>
                  <AsecMono sx={{ fontSize: 12 }}>{ep.path}</AsecMono>
                </TableCell>
                {allCategories.flatMap((cat) => ["request", "response"].map((dir) => {
                  const cell = ep.cells[`${cat}.${dir}`];
                  if (!cell) {
                    return (
                      <TableCell key={`${cat}.${dir}`} align="center" sx={{ ...asecSettingsTableBodySx, color: colors.neutral[600] }}>—</TableCell>
                    );
                  }
                  const col = tierColor(cell.tier);
                  return (
                    <TableCell key={`${cat}.${dir}`} align="center" sx={asecSettingsTableBodySx}>
                      <Box
                        component="span"
                        title={`${cat} ${dir} — tier ${cell.tier}`}
                        sx={{
                          display: "inline-block", px: 1.25, py: 0.375, borderRadius: "12px",
                          backgroundColor: col.bg, color: col.fg, fontWeight: 600, fontSize: 12,
                        }}
                      >
                        {cell.n.toLocaleString()}
                      </Box>
                    </TableCell>
                  );
                }))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

function Kv({ k, v }) {
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: "120px 1fr", gap: "6px 14px", fontSize: 12, alignItems: "center", py: 0.5 }}>
      <Typography sx={{ color: colors.neutral[700], fontWeight: 700, fontSize: 12, textTransform: "uppercase", letterSpacing: "0.04em" }}>{k}</Typography>
      <Box>{v}</Box>
    </Box>
  );
}

function DlpAuditFilterBar({
  filters, setFilters, setPage, kindChips, loading, onRefresh,
}) {
  const hasActiveFilters = Boolean(
    filters.q || filters.direction || filters.category || filters.since_hours !== 24
  );

  const clearFilters = () => {
    setFilters({ q: "", since_hours: 24, direction: null, category: null, source: null });
    setPage(0);
  };

  return (
    <Box sx={EVENT_FILTER_BAR_SX}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ gap: 1.5 }}>
        <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap" sx={{ gap: 1.5, flex: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <LayerdIcon icon={Filter} size={18} color="primary" />
            <Typography sx={{ fontSize: 14, fontWeight: 500, color: colors.neutral[800] }}>
              Filters
            </Typography>
          </Stack>

          <SafeTextField
            placeholder="Search host, path, category, or sample…"
            value={filters.q}
            onChange={(e) => { setFilters({ ...filters, q: e.target.value }); setPage(0); }}
            sx={{ ...filterInputSx, minWidth: 200, maxWidth: 320, flex: "1 1 200px" }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 16, color: colors.neutral[500] }} />
                </InputAdornment>
              ),
            }}
          />

          <Select
            value={filters.since_hours}
            onChange={(e) => { setFilters({ ...filters, since_hours: e.target.value }); setPage(0); }}
            sx={{ ...filterSelectSx, minWidth: 140 }}
          >
            <MenuItem value={1}>Last 1 hour</MenuItem>
            <MenuItem value={24}>Last 24 hours</MenuItem>
            <MenuItem value={168}>Last 7 days</MenuItem>
          </Select>

          <Select
            value={filters.direction || "all"}
            onChange={(e) => {
              const v = e.target.value;
              setFilters({ ...filters, direction: v === "all" ? null : v });
              setPage(0);
            }}
            sx={{ ...filterSelectSx, minWidth: 140 }}
          >
            <MenuItem value="all">All directions</MenuItem>
            <MenuItem value="request">Request</MenuItem>
            <MenuItem value="response">Response</MenuItem>
          </Select>

          <Select
            value={filters.category || "all"}
            onChange={(e) => {
              const v = e.target.value;
              setFilters({ ...filters, category: v === "all" ? null : v });
              setPage(0);
            }}
            sx={{ ...filterSelectSx, minWidth: 160 }}
          >
            {kindChips.map((c) => (
              <MenuItem key={c.key || "all"} value={c.key || "all"}>
                {c.label} ({c.count.toLocaleString()})
              </MenuItem>
            ))}
          </Select>

          {hasActiveFilters && (
            <Chip
              label="Clear all"
              size="small"
              onClick={clearFilters}
              sx={{
                height: 36,
                fontSize: 14,
                borderRadius: "8px",
                backgroundColor: "#fff",
                border: `1px solid ${colors.neutral[200]}`,
                color: colors.neutral[800],
                "&:hover": {
                  borderColor: colors.red[100],
                  color: colors.red[100],
                },
              }}
            />
          )}
        </Stack>

        <Stack direction="row" alignItems="center" spacing={2} sx={{ flexShrink: 0 }}>
          <RefreshIconButton
            onClick={onRefresh}
            disabled={loading}
            loading={loading}
            size="compact"
          />
        </Stack>
      </Stack>
    </Box>
  );
}

function AuditTab({ siteId, showError }) {
  const [summary, setSummary] = useState(null);
  const [list, setList] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ q: "", since_hours: 24, direction: null, category: null, source: null });
  const [page, setPage] = useState(0);
  const [openId, setOpenId] = useState(null);
  const limit = 25;

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const offset = page * limit;
      const params = { limit, offset, since_hours: filters.since_hours };
      if (filters.q) params.q = filters.q;
      if (filters.direction) params.direction = filters.direction;
      if (filters.category) params.category = filters.category;
      if (filters.source) params.source = filters.source;
      const [s, r] = await Promise.all([
        asec.dlpAuditSummary(siteId).catch(() => ({ data: null })),
        asec.dlpAudit(siteId, params),
      ]);
      const auditData = r.data;
      const rows = Array.isArray(auditData) ? auditData : (auditData?.rows || []);
      setSummary(s.data || buildAuditFallbackSummary(rows));
      setList({ rows, total: auditData?.total ?? rows.length, offset });
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || "Failed to load audit log";
      showError(msg);
      setList({ rows: [], total: 0, offset: page * limit });
    } finally {
      setLoading(false);
    }
  }, [siteId, filters, page, showError, limit]);
  useEffect(() => { reload(); }, [reload]);

  const kindChips = useMemo(() => {
    const all = summary?.top_kinds || [];
    const total = summary?.total_events_24h || 0;
    return [{ key: null, label: "All", count: total }, ...all.map((k) => ({ key: k.kind, label: k.kind, count: k.hits }))];
  }, [summary]);

  return (
    <Stack spacing={2.5}>
      {summary && <AsecMetricStrip metrics={buildAuditMetrics(summary)} />}

      <DlpAuditFilterBar
        filters={filters}
        setFilters={setFilters}
        setPage={setPage}
        kindChips={kindChips}
        loading={loading}
        onRefresh={reload}
      />

      {!list ? (
        <Box sx={{ ...ASEC_EVENTS_TABLE_CONTAINER_SX, p: 4, display: "flex", justifyContent: "center" }}>
          <CircularProgress />
        </Box>
      ) : list.rows.length === 0 ? (
        <Box sx={{ ...ASEC_EVENTS_TABLE_CONTAINER_SX, py: 4.5, px: 2.25, textAlign: "center" }}>
          <Box sx={{ width: 44, height: 44, borderRadius: "11px", mx: "auto", mb: 1, backgroundColor: colors.neutral[100], display: "flex", alignItems: "center", justifyContent: "center" }}>
            <LayerdIcon icon={List} size="md" color="neutral" />
          </Box>
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: colors.neutral[1000], mb: 0.5 }}>No audit rows match</Typography>
          <Typography sx={{ fontSize: 12, color: colors.neutral[700], maxWidth: 380, mx: "auto", lineHeight: "18px" }}>
            Layerd samples detections into this table at the rate configured by <strong>policy.audit_sampling_rate</strong>.
            Try widening the time range or clearing chips.
          </Typography>
        </Box>
      ) : (
        <AsecEventsTableShell
          footer={(
            <AsecEventsTableFooter
              from={list.offset + 1}
              to={Math.min(list.offset + list.rows.length, list.total)}
              total={list.total}
              label="events"
              page={page}
              pageCount={Math.ceil(list.total / limit)}
              onPageChange={setPage}
            />
          )}
        >
          <TableHead>
            <TableRow>
              {["Time", "Host", "Path", "Direction", "Kind", "Count", ""].map((h, i) => (
                <AsecEventsHeadCell key={h || "expand"} align={i === 5 ? "right" : "left"}>{h}</AsecEventsHeadCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {list.rows.map((row) => {
              const open = openId === row.id;
              const ageStr = relative(row.detected_at);
              const isHigh = row.sensitivity === "high" || row.sensitivity === "critical";
              return (
                <React.Fragment key={row.id}>
                  <TableRow sx={asecEventRowSx} onClick={() => setOpenId(open ? null : row.id)}>
                    <TableCell>
                      <Stack>
                        <AsecEventsBodyText sx={{ fontSize: 12 }}>
                          {row.detected_at ? new Date(row.detected_at).toLocaleTimeString() : "—"}
                        </AsecEventsBodyText>
                        <Typography sx={{ fontSize: 12, color: colors.neutral[700] }}>{ageStr}</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <AsecEventsBodyText sx={{ fontSize: 12 }}>{row.host || "—"}</AsecEventsBodyText>
                    </TableCell>
                    <TableCell>
                      <AsecEventsBodyText sx={{ fontSize: 12 }}>{row.path || "—"}</AsecEventsBodyText>
                    </TableCell>
                    <TableCell>
                      {row.direction ? (
                        <Box sx={{
                          display: "inline-flex", alignItems: "center", gap: 0.625, fontSize: 12,
                          fontWeight: 500, px: 1.125, py: 0.375, borderRadius: "5px",
                          backgroundColor: row.direction === "response" ? colors.primary[100] : "#FFF6D0",
                          color: row.direction === "response" ? colors.primary[500] : "#7a5b10",
                                                  }}>
                          <LayerdIcon icon={row.direction === "response" ? ArrowDown : ArrowUp} size="xs" color="inherit" />
                          {row.direction === "response" ? "resp" : "req"}
                        </Box>
                      ) : "—"}
                    </TableCell>
                    <TableCell>
                      <Box sx={{
                        fontSize: 12, fontWeight: 600,
                        px: 0.875, py: 0.25, borderRadius: "5px", display: "inline-flex", alignItems: "center", gap: 0.375,
                        backgroundColor: isHigh ? "#FEECEE" : "rgba(99,113,247,0.10)",
                        color: isHigh ? colors.red[200] : colors.primary[500],
                        border: `1px solid ${isHigh ? "#FBC9CF" : "rgba(99,113,247,0.20)"}`,
                      }}>
                        {row.category}
                        <Box component="span" sx={{ color: colors.neutral[700], fontWeight: 500, backgroundColor: "#fff", px: 0.5, borderRadius: "3px", fontSize: 12 }}>
                          {row.sample_count}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <AsecEventsBodyText sx={{ fontWeight: 600 }}>{row.sample_count}</AsecEventsBodyText>
                    </TableCell>
                    <TableCell sx={{ width: 48 }}>
                      <ChevronRightIcon sx={{ fontSize: 16, color: colors.neutral[600], transform: open ? "rotate(90deg)" : "none", transition: "transform 0.15s" }} />
                    </TableCell>
                  </TableRow>
                  {open && (
                    <TableRow>
                      <TableCell colSpan={7} sx={{ p: 0, backgroundColor: colors.neutral[50], borderTop: `1px solid ${colors.neutral[200]}` }}>
                        <Grid container>
                          <Grid item xs={12} md={7} sx={{ p: "16px 18px", borderRight: { md: `1px solid ${colors.neutral[200]}` } }}>
                            <Typography sx={{ mb: 1, fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: colors.neutral[1000] }}>
                              Redacted sample
                            </Typography>
                            <Box component="pre" sx={{
                              fontSize: 12, lineHeight: "18px", backgroundColor: "#0E1116",
                              color: "#E6EBF2", p: "12px 14px", borderRadius: "8px", m: 0,
                              whiteSpace: "pre-wrap", wordBreak: "break-all",
                            }}>
                              {row.sample || "(no sample stored)"}
                            </Box>
                          </Grid>
                          <Grid item xs={12} md={5} sx={{ p: "16px 18px" }}>
                            <Typography sx={{ mb: 1, fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: colors.neutral[1000] }}>
                              Event details
                            </Typography>
                            <Kv k="Request ID" v={row.request_id ? <AsecMono>{row.request_id}</AsecMono> : "—"} />
                            <Kv k="Method" v={row.method ? <AsecMono>{row.method}</AsecMono> : "—"} />
                            <Kv k="Status" v={row.status ? (
                              <AsecMono sx={{ color: row.status < 300 ? colors.green[300] : colors.red[200], fontWeight: 600 }}>{row.status}</AsecMono>
                            ) : "—"} />
                            <Kv k="Source" v={<AsecStatusPill kind="indigo">{row.source}</AsecStatusPill>} />
                            <Kv k="Sensitivity" v={row.sensitivity} />
                            <Kv k="Detected" v={formatDate(row.detected_at) || "—"} />
                          </Grid>
                        </Grid>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </AsecEventsTableShell>
      )}
    </Stack>
  );
}

function relative(iso) {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return `${Math.round(ms / 1000)}s ago`;
  if (ms < 3_600_000) return `${Math.round(ms / 60_000)}m ago`;
  if (ms < 86_400_000) return `${Math.round(ms / 3_600_000)}h ago`;
  return `${Math.round(ms / 86_400_000)}d ago`;
}

function Body({ siteId, onRegisterReload, onRegisterFocusTest, onDictionaryTabActive }) {
  const { showSuccess, showError } = useNotification();
  const { canConfigure } = useAsecPermissions();
  const [tab, setTab] = useState(0);
  const patternInputRef = React.useRef(null);

  const [policy, setPolicy] = useState(null);
  const [savedPolicy, setSavedPolicy] = useState(null);
  const [summary, setSummary] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [categories, setCategories] = useState(null);
  const [sample, setSample] = useState(null);
  const [dict, setDict] = useState(null);
  const [savedDict, setSavedDict] = useState(null);
  const [dictStats, setDictStats] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (onDictionaryTabActive) onDictionaryTabActive(TAB_KEYS[tab] === "dictionary");
  }, [tab, onDictionaryTabActive]);

  useEffect(() => {
    if (onRegisterFocusTest) {
      onRegisterFocusTest(() => {
        setTab(1);
        setTimeout(() => patternInputRef.current?.focus?.(), 50);
      });
    }
  }, [onRegisterFocusTest]);

  const reloadAll = useCallback(async () => {
    if (!siteId) return;
    try {
      const safe = async (fn, fallback) => {
        try { return await fn(); } catch { return fallback; }
      };
      const [p, s, c, cs, sm, d, ds] = await Promise.all([
        asec.dlpPolicy(siteId),
        safe(() => asec.dlpSummary(siteId), { data: null }),
        safe(() => asec.dlpCatalog(siteId), { data: null }),
        safe(() => asec.dlpCategoryStats(siteId), { data: null }),
        safe(() => asec.dlpSample(siteId), { data: null }),
        asec.dlpDictList(siteId).catch(() => ({ data: [] })),
        safe(() => asec.dlpDictStats(siteId), { data: {} }),
      ]);
      const nextPolicy = p.data;
      setPolicy(nextPolicy);
      setSavedPolicy(nextPolicy);
      setSummary(s.data || buildFallbackSummary(nextPolicy, d.data));
      setCatalog(c.data || buildFallbackCatalog());
      setCategories(Array.isArray(cs.data) && cs.data.length ? cs.data : buildFallbackCategories());
      setSample(sm.data);
      const rows = Array.isArray(d.data) ? d.data.map(normalizeDictRow) : [];
      setDict(rows);
      setSavedDict(rows.map((r) => ({ ...r })));
      setDictStats(ds.data || {});
    } catch (e) {
      const msg = e?.response?.data?.message
        || (typeof e?.response?.data === "string" && !e.response.data.startsWith("<!") ? e.response.data : null)
        || e?.message
        || "Failed to load DLP policy";
      showError(msg);
    }
  }, [siteId, showError]);

  useEffect(() => { reloadAll(); }, [reloadAll]);

  useEffect(() => {
    if (onRegisterReload) onRegisterReload(reloadAll);
  }, [onRegisterReload, reloadAll]);

  const policyModifiedFields = useMemo(() => {
    if (!policy || !savedPolicy) return [];
    return collectObjectDiff(pickPolicyForPut(savedPolicy), pickPolicyForPut(policy), DLP_POLICY_DIFF_FIELDS);
  }, [policy, savedPolicy]);
  const policyModifiedCount = policyModifiedFields.length;

  const dictModifiedFields = useMemo(() => {
    if (!dict || !savedDict) return [];
    const savedById = Object.fromEntries(savedDict.map((r) => [r.id, r]));
    return dict
      .filter((r) => savedById[r.id]?.enabled !== r.enabled)
      .map((r) => fieldChange(
        r.pattern || r.description || "Dictionary pattern",
        enabledLabel(savedById[r.id]?.enabled),
        enabledLabel(r.enabled),
      ));
  }, [dict, savedDict]);
  const dictModifiedCount = dictModifiedFields.length;

  const onSavePolicy = useCallback((patch) => {
    if (!canConfigure) return;
    setPolicy((prev) => ({ ...prev, ...patch }));
  }, [canConfigure]);

  const onRevertPolicy = () => setPolicy(savedPolicy);

  const onRevertDict = useCallback(() => {
    if (savedDict) setDict(savedDict.map((r) => ({ ...r })));
  }, [savedDict]);

  const toggleDictEnabled = useCallback((row) => {
    if (!canConfigure) return;
    setDict((prev) => (prev || []).map((r) => (
      r.id === row.id ? { ...r, enabled: !r.enabled } : r
    )));
  }, [canConfigure]);

  const reloadDict = useCallback(() => {
    if (!siteId) return;
    Promise.all([
      asec.dlpDictList(siteId).catch(() => ({ data: [] })),
      asec.dlpDictStats(siteId).catch(() => ({ data: {} })),
    ]).then(([list, stats]) => {
      const rows = Array.isArray(list.data) ? list.data.map(normalizeDictRow) : [];
      setDict(rows);
      setSavedDict(rows.map((r) => ({ ...r })));
      setDictStats(stats.data || {});
    });
  }, [siteId]);

  const applyPolicy = useCallback(async () => {
    if (!siteId || !policy || !canConfigure) return;
    setSaving(true);
    try {
      const body = pickPolicyForPut(policy);
      const r = await asec.dlpPolicySet(siteId, body);
      setPolicy(r.data);
      setSavedPolicy(r.data);
      showSuccess("Saved");
      asec.dlpSummary(siteId).then((s) => setSummary(s.data));
    } catch (e) {
      showError(e?.response?.data?.message || e?.message || String(e));
    } finally {
      setSaving(false);
    }
  }, [policy, siteId, showError, showSuccess, canConfigure]);

  const applyDictionary = useCallback(async () => {
    if (!siteId || !dict || !savedDict || !canConfigure || dictModifiedCount === 0) return;
    setSaving(true);
    try {
      const savedById = Object.fromEntries(savedDict.map((r) => [r.id, r.enabled]));
      const changed = dict.filter((r) => savedById[r.id] !== r.enabled);
      await Promise.all(
        changed.map((r) => asec.dlpDictPatch(siteId, r.id, { enabled: r.enabled }))
      );
      const next = dict.map((r) => ({ ...r }));
      setSavedDict(next);
      showSuccess("Saved");
      asec.dlpDictStats(siteId).then((r) => setDictStats(r.data || {}));
    } catch (e) {
      showError(e?.response?.data?.message || e?.message || String(e));
    } finally {
      setSaving(false);
    }
  }, [canConfigure, dict, dictModifiedCount, savedDict, showError, showSuccess, siteId]);

  const onDiscardChanges = useCallback(() => {
    if (TAB_KEYS[tab] === "policy") onRevertPolicy();
    else if (TAB_KEYS[tab] === "dictionary") onRevertDict();
  }, [onRevertDict, onRevertPolicy, tab]);

  const onApplyChanges = useCallback(async () => {
    if (TAB_KEYS[tab] === "policy") await applyPolicy();
    else if (TAB_KEYS[tab] === "dictionary") await applyDictionary();
  }, [applyDictionary, applyPolicy, tab]);

  const stickyModifiedFields = TAB_KEYS[tab] === "policy"
    ? policyModifiedFields
    : TAB_KEYS[tab] === "dictionary"
      ? dictModifiedFields
      : [];

  const stickyModifiedCount = stickyModifiedFields.length;

  const stickyOpen = canConfigure && stickyModifiedCount > 0 && (
    TAB_KEYS[tab] === "policy" || TAB_KEYS[tab] === "dictionary"
  );

  if (!siteId) return null;
  if (!policy) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const dictCount = (dict || []).length;
  const auditCount = summary?.detections_24h ?? 0;

  return (
    <Stack spacing={4}>
      <AsecMetricStrip metrics={buildHeroMetrics(summary)} />

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ ...asecTabsSx, mb: 0.5 }}>
        <Tab label="Policy" icon={<LayerdIcon icon={SlidersHorizontal} size="sm" color="inherit" />} iconPosition="start" />
        <Tab label={`Dictionary${dictCount ? ` (${dictCount})` : ""}`} icon={<LayerdIcon icon={BookMarked} size="sm" color="inherit" />} iconPosition="start" />
        <Tab label={`Audit log${auditCount ? ` (${auditCount})` : ""}`} icon={<LayerdIcon icon={List} size="sm" color="inherit" />} iconPosition="start" />
        <Tab label="Sensitivity" icon={<LayerdIcon icon={Shield} size="sm" color="inherit" />} iconPosition="start" />
      </Tabs>

      {TAB_KEYS[tab] === "policy" && (
        <PolicyTab
          siteId={siteId}
          policy={policy}
          categories={categories}
          sample={sample}
          onSave={onSavePolicy}
          canConfigure={canConfigure}
        />
      )}
      {TAB_KEYS[tab] === "dictionary" && (
        <DictionaryTab
          siteId={siteId}
          dict={dict}
          dictStats={dictStats}
          catalog={catalog}
          reload={reloadDict}
          onToggleEnabled={toggleDictEnabled}
          showSuccess={showSuccess}
          showError={showError}
          patternInputRef={patternInputRef}
        />
      )}
      {TAB_KEYS[tab] === "audit" && <AuditTab siteId={siteId} showError={showError} />}
      {TAB_KEYS[tab] === "sensitivity" && <SensitivityTab siteId={siteId} showError={showError} />}

      <StickySaveBar
        open={stickyOpen}
        modifiedCount={stickyModifiedCount}
        modifiedFields={stickyModifiedFields}
        onDiscard={onDiscardChanges}
        onApply={onApplyChanges}
        isApplying={saving}
      />
    </Stack>
  );
}

function pickPolicyForPut(p) {
  return {
    mode: p.mode,
    enabled_categories: p.enabled_categories,
    replacement: p.replacement,
    raw_retention_seconds: p.raw_retention_seconds,
    aws_region: p.aws_region,
    aws_role_arn: p.aws_role_arn,
    tag_alert: !!p.tag_alert,
    block_request_pii: !!p.block_request_pii,
    scope: p.scope,
    audit_sampling_rate: typeof p.audit_sampling_rate === "number" ? p.audit_sampling_rate : 0,
  };
}

const FALLBACK_CATEGORIES = [
  { key: "personal_identity", label: "Personal identity", icon: "id-card", sensitivity: "high", pattern_count: 14, examples: "Full names, DOB, age", hits_24h: 0 },
  { key: "contact", label: "Contact", icon: "at-sign", sensitivity: "med", pattern_count: 11, examples: "Email, phone, postal address", hits_24h: 0 },
  { key: "government_ids", label: "Government IDs", icon: "badge-info", sensitivity: "high", pattern_count: 9, examples: "SSN, passport, tax IDs", hits_24h: 0 },
  { key: "financial", label: "Financial", icon: "credit-card", sensitivity: "high", pattern_count: 12, examples: "Cards, IBAN, routing numbers", hits_24h: 0 },
  { key: "digital_secrets", label: "Digital secrets", icon: "key-round", sensitivity: "high", pattern_count: 8, examples: "API keys, tokens, passwords", hits_24h: 0 },
  { key: "health", label: "Health", icon: "stethoscope", sensitivity: "high", pattern_count: 7, examples: "MRN, diagnosis codes", hits_24h: 0 },
  { key: "location", label: "Location", icon: "map-pin", sensitivity: "med", pattern_count: 5, examples: "GPS coords, precise addresses", hits_24h: 0 },
  { key: "demographics", label: "Demographics", icon: "users-round", sensitivity: "low", pattern_count: 4, examples: "Gender, ethnicity, religion", hits_24h: 0 },
  { key: "employment", label: "Employment", icon: "briefcase", sensitivity: "med", pattern_count: 6, examples: "Employee IDs, payroll refs", hits_24h: 0 },
];

function buildFallbackCategories() {
  return FALLBACK_CATEGORIES;
}

function buildFallbackCatalog() {
  return {
    categories: FALLBACK_CATEGORIES,
    patterns: FALLBACK_CATEGORIES.flatMap((c) => (
      Array.from({ length: c.pattern_count }, (_, i) => ({ category: c.key, name: `${c.key}_${i + 1}` }))
    )),
  };
}

function buildFallbackSummary(policy, dict) {
  const customCount = Array.isArray(dict) ? dict.length : 0;
  const builtinCount = FALLBACK_CATEGORIES.reduce((n, c) => n + c.pattern_count, 0);
  return {
    mode: policy?.mode || "off",
    tag_alert: !!policy?.tag_alert,
    replacement: policy?.replacement || "tagged",
    raw_retention_seconds: policy?.raw_retention_seconds ?? 300,
    audit_sampling_rate: policy?.audit_sampling_rate ?? 0,
    responses_scanned_24h: null,
    detections_24h: 0,
    detections_pct: null,
    top_category: null,
    custom_pattern_count: customCount,
    builtin_pattern_count: builtinCount,
    builtin_category_count: FALLBACK_CATEGORIES.length,
  };
}

function normalizeDictRow(row) {
  return { ...row, enabled: row.enabled !== false };
}

function buildAuditFallbackSummary(rows) {
  const total = (rows || []).reduce((n, r) => n + (r.sample_count || 1), 0);
  const kindCounts = {};
  for (const row of rows || []) {
    kindCounts[row.category] = (kindCounts[row.category] || 0) + (row.sample_count || 1);
  }
  const topKinds = Object.entries(kindCounts)
    .map(([kind, hits]) => ({ kind, hits }))
    .sort((a, b) => b.hits - a.hits);
  return {
    total_events_24h: total,
    total_rewrites_24h: total,
    hosts_with_detections: [],
    top_kinds: topKinds,
  };
}

export default function AsecDlp() {
  const reloadRef = React.useRef(null);
  const focusTestRef = React.useRef(null);
  const [dictionaryTabActive, setDictionaryTabActive] = React.useState(false);

  return (
    <AsecPage
      featureKey="api.dlp"
      title="DLP"
      helmetTitle="API Security — DLP"
      helpTooltip="Inline PII redaction. Two-tier: an inline regex+entropy classifier (sub-50us) handles structured patterns; an async AWS Comprehend worker catches free-text PII via shadow tables."
      pageDescription="Inline PII detection and redaction — regex classifiers plus optional AWS Comprehend for free-text fields."
      onRefresh={async () => {
        if (reloadRef.current) await reloadRef.current();
      }}
      headerActions={dictionaryTabActive ? (
        <PrimaryButton
          startIcon={<LayerdIcon icon={Play} size="sm" color="inherit" />}
          onClick={() => {
            focusTestRef.current?.();
          }}
        >
          Test pattern
        </PrimaryButton>
      ) : null}
    >
      {(siteId) => (
        <Body
          siteId={siteId}
          onRegisterReload={(fn) => { reloadRef.current = fn; }}
          onDictionaryTabActive={setDictionaryTabActive}
          onRegisterFocusTest={(fn) => { focusTestRef.current = fn; }}
        />
      )}
    </AsecPage>
  );
}
